import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface ProofingRowData {
  variety: string;
  proofingTime: string;
  prooferTemp: string;
  rhProofer: string;
}

export interface ProofingCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNumber: string;
  observedtime: string;
  standardProofingTime: string;
  rows: ProofingRowData[];
}

interface OfflineSavedData {
  cycleNo: number;
  records: ProofingCycleData[];
  timestamp: number;
}

interface ProofingState {
  fetchedCycles: ProofingCycleData[];
  offlineSavedData: OfflineSavedData[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastFetchTimestamp: number | null;
}

const initialState: ProofingState = {
  fetchedCycles: [],
  offlineSavedData: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

const proofingSlice = createSlice({
  name: 'proofing',
  initialState,
  reducers: {
    addOfflineData: (state, action: PayloadAction<OfflineSavedData>) => {
      state.offlineSavedData.push(action.payload);
    },
    clearOfflineData: (state) => {
      state.offlineSavedData = [];
    },
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        (item) => item.cycleNo !== action.payload
      );
    },
    setFetchedCycles: (state, action: PayloadAction<ProofingCycleData[]>) => {
      state.fetchedCycles = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
  },
});

export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
} = proofingSlice.actions;

export const selectProofingState = (state: { proofing: ProofingState }) =>
  state.proofing;
export const selectOfflineDataCount = (state: { proofing: ProofingState }) =>
  state.proofing.offlineSavedData.length;
export const selectFetchedCycles = (state: { proofing: ProofingState }) =>
  state.proofing.fetchedCycles;

export default proofingSlice.reducer;
